import { motion, AnimatePresence } from "framer-motion";
import { Settings, X, Palette, Sparkles, Music, Zap, Type, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useState } from "react";

export interface ValentineSettings {
  themeColor: "pink" | "red" | "purple" | "roseGold";
  backgroundStyle: "aurora" | "particles" | "stars" | "neonGrid";
  heartStyle: "emoji" | "neon" | "classic" | "mixed";
  musicTrack: "lofi" | "piano" | "bollywood" | "pop";
  intensity: "subtle" | "normal" | "chaos";
  fontStyle: "cursive" | "bold" | "handwritten" | "neon";
  celebrationStyle: "fireworks" | "confetti" | "hearts" | "all";
}

interface CustomizationPanelProps {
  settings: ValentineSettings;
  onSettingsChange: (settings: ValentineSettings) => void;
  isOpen: boolean;
  onToggle: () => void;
}

const CustomizationPanel = ({ settings, onSettingsChange, isOpen, onToggle }: CustomizationPanelProps) => {
  const updateSetting = <K extends keyof ValentineSettings>(key: K, value: ValentineSettings[K]) => {
    onSettingsChange({ ...settings, [key]: value });
  };

  const themeColors = [
    { id: "pink", label: "Pink", color: "bg-pink-500" },
    { id: "red", label: "Red", color: "bg-red-500" },
    { id: "purple", label: "Purple", color: "bg-purple-500" },
    { id: "roseGold", label: "Rose Gold", color: "bg-gradient-to-r from-pink-400 to-amber-300" },
  ];

  const backgroundStyles = [
    { id: "aurora", label: "Aurora", icon: "🌌" },
    { id: "particles", label: "Particles", icon: "✨" },
    { id: "stars", label: "Stars", icon: "⭐" },
    { id: "neonGrid", label: "Neon Grid", icon: "🔲" },
  ];

  const heartStyles = [
    { id: "emoji", label: "Emoji", preview: "💖" },
    { id: "neon", label: "Neon", preview: "💗" },
    { id: "classic", label: "Classic", preview: "❤️" },
    { id: "mixed", label: "Mixed", preview: "💕" },
  ];

  const musicTracks = [
    { id: "lofi", label: "Lo-fi Romance", icon: "🎵" },
    { id: "piano", label: "Piano Serenade", icon: "🎹" },
    { id: "bollywood", label: "Bollywood Love", icon: "🎬" },
    { id: "pop", label: "Pop Romance", icon: "🎤" },
  ];

  const intensityLevels = [
    { id: "subtle", label: "Subtle", description: "Elegant & calm" },
    { id: "normal", label: "Normal", description: "Balanced effects" },
    { id: "chaos", label: "Maximum Chaos", description: "EVERYTHING!" },
  ];

  const fontStyles = [
    { id: "cursive", label: "Cursive", className: "font-serif italic" },
    { id: "bold", label: "Bold", className: "font-bold" },
    { id: "handwritten", label: "Handwritten", className: "font-serif" },
    { id: "neon", label: "Neon Glow", className: "font-bold" },
  ];

  const celebrationStyles = [
    { id: "fireworks", label: "Fireworks", icon: "🎆" },
    { id: "confetti", label: "Confetti", icon: "🎊" },
    { id: "hearts", label: "Hearts", icon: "💕" },
    { id: "all", label: "All Effects!", icon: "🎉" },
  ];

  return (
    <>
      {/* Settings Toggle Button */}
      <motion.button
        onClick={onToggle}
        className="fixed top-4 left-4 z-50 p-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-all group"
        whileHover={{ scale: 1.1, rotate: 15 }}
        whileTap={{ scale: 0.9 }}
      >
        <Settings className="w-5 h-5 text-pink-400 group-hover:text-pink-300 transition-colors" />
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onToggle}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            />

            {/* Panel Content */}
            <motion.div
              initial={{ x: -400, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -400, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              className="fixed left-0 top-0 bottom-0 w-80 sm:w-96 bg-gradient-to-b from-slate-900/95 via-pink-950/90 to-slate-900/95 backdrop-blur-xl border-r border-pink-500/20 z-50 overflow-y-auto"
            >
              {/* Header */}
              <div className="sticky top-0 bg-slate-900/80 backdrop-blur-md p-4 border-b border-pink-500/20 flex items-center justify-between">
                <h2 className="text-xl font-bold text-pink-300 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Customize
                </h2>
                <button
                  onClick={onToggle}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5 text-pink-400" />
                </button>
              </div>

              <div className="p-4 space-y-6">
                {/* Theme Color */}
                <div className="space-y-3">
                  <label className="text-pink-200 font-medium flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Theme Color
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {themeColors.map((theme) => (
                      <button
                        key={theme.id}
                        onClick={() => updateSetting("themeColor", theme.id as ValentineSettings["themeColor"])}
                        className={`p-3 rounded-xl ${theme.color} transition-all ${
                          settings.themeColor === theme.id
                            ? "ring-2 ring-white ring-offset-2 ring-offset-slate-900 scale-110"
                            : "opacity-60 hover:opacity-100"
                        }`}
                        title={theme.label}
                      />
                    ))}
                  </div>
                </div>

                {/* Background Style */}
                <div className="space-y-3">
                  <label className="text-pink-200 font-medium flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Background Style
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {backgroundStyles.map((style) => (
                      <button
                        key={style.id}
                        onClick={() => updateSetting("backgroundStyle", style.id as ValentineSettings["backgroundStyle"])}
                        className={`p-3 rounded-xl bg-white/5 border transition-all flex items-center gap-2 ${
                          settings.backgroundStyle === style.id
                            ? "border-pink-500 bg-pink-500/20"
                            : "border-white/10 hover:border-pink-500/50"
                        }`}
                      >
                        <span className="text-xl">{style.icon}</span>
                        <span className="text-sm text-pink-200">{style.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Heart Style */}
                <div className="space-y-3">
                  <label className="text-pink-200 font-medium">Heart Style</label>
                  <div className="grid grid-cols-4 gap-2">
                    {heartStyles.map((style) => (
                      <button
                        key={style.id}
                        onClick={() => updateSetting("heartStyle", style.id as ValentineSettings["heartStyle"])}
                        className={`p-3 rounded-xl bg-white/5 border transition-all text-center ${
                          settings.heartStyle === style.id
                            ? "border-pink-500 bg-pink-500/20"
                            : "border-white/10 hover:border-pink-500/50"
                        }`}
                      >
                        <span className="text-2xl">{style.preview}</span>
                        <span className="block text-xs text-pink-300 mt-1">{style.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Music Selection */}
                <div className="space-y-3">
                  <label className="text-pink-200 font-medium flex items-center gap-2">
                    <Music className="w-4 h-4" />
                    Music Track
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {musicTracks.map((track) => (
                      <button
                        key={track.id}
                        onClick={() => updateSetting("musicTrack", track.id as ValentineSettings["musicTrack"])}
                        className={`p-3 rounded-xl bg-white/5 border transition-all flex items-center gap-2 ${
                          settings.musicTrack === track.id
                            ? "border-pink-500 bg-pink-500/20"
                            : "border-white/10 hover:border-pink-500/50"
                        }`}
                      >
                        <span className="text-lg">{track.icon}</span>
                        <span className="text-sm text-pink-200">{track.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Animation Intensity */}
                <div className="space-y-3">
                  <label className="text-pink-200 font-medium flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Animation Intensity
                  </label>
                  <div className="space-y-2">
                    {intensityLevels.map((level) => (
                      <button
                        key={level.id}
                        onClick={() => updateSetting("intensity", level.id as ValentineSettings["intensity"])}
                        className={`w-full p-3 rounded-xl bg-white/5 border transition-all text-left ${
                          settings.intensity === level.id
                            ? "border-pink-500 bg-pink-500/20"
                            : "border-white/10 hover:border-pink-500/50"
                        }`}
                      >
                        <span className="text-pink-200 font-medium">{level.label}</span>
                        <span className="block text-xs text-pink-400/70">{level.description}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Font Style */}
                <div className="space-y-3">
                  <label className="text-pink-200 font-medium flex items-center gap-2">
                    <Type className="w-4 h-4" />
                    Font Style
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {fontStyles.map((font) => (
                      <button
                        key={font.id}
                        onClick={() => updateSetting("fontStyle", font.id as ValentineSettings["fontStyle"])}
                        className={`p-3 rounded-xl bg-white/5 border transition-all ${
                          settings.fontStyle === font.id
                            ? "border-pink-500 bg-pink-500/20"
                            : "border-white/10 hover:border-pink-500/50"
                        }`}
                      >
                        <span className={`text-pink-200 ${font.className}`}>{font.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Celebration Style */}
                <div className="space-y-3">
                  <label className="text-pink-200 font-medium flex items-center gap-2">
                    <PartyPopper className="w-4 h-4" />
                    Celebration Style
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {celebrationStyles.map((style) => (
                      <button
                        key={style.id}
                        onClick={() => updateSetting("celebrationStyle", style.id as ValentineSettings["celebrationStyle"])}
                        className={`p-3 rounded-xl bg-white/5 border transition-all flex items-center gap-2 ${
                          settings.celebrationStyle === style.id
                            ? "border-pink-500 bg-pink-500/20"
                            : "border-white/10 hover:border-pink-500/50"
                        }`}
                      >
                        <span className="text-xl">{style.icon}</span>
                        <span className="text-sm text-pink-200">{style.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default CustomizationPanel;
